import { appendFile, mkdir } from 'node:fs/promises';
import { dirname } from 'node:path';
import OpenAI from 'openai';
import type { Model, ModelMetadata, ModelProvider } from '../../model';
import { OpenAIChatCompletionsModel } from '../openai/chat-completions-model';
import {
  getDefaultHepAIBaseURL,
  getDefaultHepAIKey,
  getDefaultHepAIModel,
} from './defaults';

const RAW_HEPAI_HTTP_DEBUG_FILE = '/tmp/lyra-provider-http.ndjson';

type DebugFetchInit = NonNullable<Parameters<typeof fetch>[1]>;

export type HepAIModelMetadataResolver =
  | Record<string, ModelMetadata>
  | ((modelName: string) => ModelMetadata | undefined);

export type HepAIProviderOptions = {
  apiKey?: string;
  baseURL?: string;
  defaultModel?: string;
  openAIClient?: OpenAI;
  modelMetadata?: HepAIModelMetadataResolver;
};

export class HepAIProvider implements ModelProvider {
  #client?: OpenAI;
  #modelCache = new Map<string, Model>();
  #options: HepAIProviderOptions;

  constructor(options: HepAIProviderOptions = {}) {
    this.#options = options;
    if (this.#options.openAIClient) {
      if (this.#options.apiKey)
        throw new Error('Cannot provide both apiKey and openAIClient');
      if (this.#options.baseURL)
        throw new Error('Cannot provide both baseURL and openAIClient');
      this.#client = this.#options.openAIClient;
    }
  }

  getDefaultModelName(): string {
    return this.#options.defaultModel || getDefaultHepAIModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || this.getDefaultModelName();
    return resolveConfiguredModelMetadata(
      this.#options.modelMetadata,
      resolvedModelName,
    );
  }

  #getClient(): OpenAI {
    if (!this.#client) {
      this.#client = new OpenAI({
        apiKey: this.#options.apiKey ?? getDefaultHepAIKey(),
        baseURL: this.#options.baseURL ?? getDefaultHepAIBaseURL(),
        fetch: createHepAIRawDebugFetch(),
      });
    }
    return this.#client;
  }

  async getModel(modelName?: string | undefined): Promise<Model> {
    const model = modelName || this.getDefaultModelName();
    const cacheKey = JSON.stringify([model, 'hepai_chat_completions']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) return cachedModel;

    const resolvedModel = new OpenAIChatCompletionsModel(
      this.#getClient(),
      model,
      this.getModelMetadata(model),
    );
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  /**
   * Closes cached model wrappers and clears cache.
   */
  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close HepAIProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function createHepAIRawDebugFetch(): typeof fetch {
  return async (input, init) => {
    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    await writeHepAIRawDebug({
      type: 'http_request',
      requestId,
      url: getDebugUrl(input),
      method: init?.method,
      headers: getDebugHeaders(init?.headers),
      body: getDebugBody(init?.body),
    });

    let response: Response;
    try {
      response = await fetch(input, init);
    } catch (error) {
      await writeHepAIRawDebug({
        type: 'http_error',
        requestId,
        error,
      });
      throw error;
    }

    const clonedResponse = response.clone();
    void clonedResponse
      .text()
      .then((body) =>
        writeHepAIRawDebug({
          type: 'http_response',
          requestId,
          status: response.status,
          statusText: response.statusText,
          headers: getDebugHeaders(response.headers),
          body,
        }),
      )
      .catch((error) =>
        writeHepAIRawDebug({
          type: 'http_response_read_error',
          requestId,
          status: response.status,
          statusText: response.statusText,
          error,
        }),
      );

    return response;
  };
}

async function writeHepAIRawDebug(
  entry: Record<string, unknown>,
): Promise<void> {
  try {
    await mkdir(dirname(RAW_HEPAI_HTTP_DEBUG_FILE), { recursive: true });
    await appendFile(
      RAW_HEPAI_HTTP_DEBUG_FILE,
      `${JSON.stringify({ timestamp: new Date().toISOString(), ...entry }, hepAIRawDebugReplacer)}\n`,
      'utf8',
    );
  } catch {
    // Temporary debug logging must not alter model execution.
  }
}

function getDebugUrl(input: Parameters<typeof fetch>[0]): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

function getDebugHeaders(
  headers: DebugFetchInit['headers'] | undefined,
): Record<string, string> | undefined {
  if (!headers) {
    return undefined;
  }
  const result: Record<string, string> = {};
  const entries =
    headers instanceof Headers
      ? headers.entries()
      : Array.isArray(headers)
        ? headers
        : Object.entries(headers);
  for (const [key, value] of entries) {
    result[key] = shouldRedactHeader(key) ? '<redacted>' : String(value);
  }
  return result;
}

function shouldRedactHeader(key: string): boolean {
  return /authorization|api[-_]key|cookie/i.test(key);
}

function getDebugBody(
  body: DebugFetchInit['body'] | null | undefined,
): unknown {
  if (body == null) {
    return body;
  }
  if (typeof body === 'string') {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body.toString();
  }
  if (body instanceof ArrayBuffer) {
    return Buffer.from(body).toString('utf8');
  }
  if (ArrayBuffer.isView(body)) {
    return Buffer.from(body.buffer, body.byteOffset, body.byteLength).toString(
      'utf8',
    );
  }
  return `[${body.constructor?.name ?? typeof body}]`;
}

function hepAIRawDebugReplacer(_key: string, value: unknown): unknown {
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  return value;
}

function resolveConfiguredModelMetadata(
  resolver: HepAIModelMetadataResolver | undefined,
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}
